
import Vue from "vue";
import Sale from "@/models/base/sale";
import { base64ToBlob, downloadBlob } from "@/utils/blob";
import { ClientPortalService, MollieAutomaticPaymentStatus } from "@/services/client-portal-service";
import AppSignSaleModal from "@/components/client-portal/SignSaleModal.vue";
import AppTicketPreview from "@/components/tickets/TicketPreview.vue";
import { ISelectItem } from "winfakt-vue-components";
import AppPdfViewer from "@/components/common/PdfViewer.vue";
import { PdfImages } from "@/utils/pdf-images";
import { BrowserInfo } from "@/utils/browser";

export default Vue.extend({
	data() {
		return {
			sale: new Sale(),
			canPayWithMollie: false,
			mollieAutomaticPaymentStatus: MollieAutomaticPaymentStatus.UNAVAILABLE,
			canPayWithQrCode: false,
			canSign: false,
			signModalOpen: false,
			ticketModalOpen: false,
			hideToPayAmount: false,
			pdfImages: null as null | PdfImages,
			blob: null as null | Blob
		};
	},
	computed: {
		// eslint-disable-next-line max-lines-per-function
		toolbarActions(): ISelectItem<Function>[] {
			let a = [{
				text: this.$ct("common.download").toString(),
				value: this.downloadFile,
				icon: "envelope"
			}];
			if (this.blob){
				a.push({
					text: this.$ct("common.print").toString(),
					value: this.print,
					icon: "print"
				});
			}

			if (this.canPayWithMollie) {
				a.push(
					{
						text: this.$ct("common.pay").toString(),
						value: this.pay,
						icon: "credit-card"
					}
				);
			}

			if (this.canCreateAutomaticPaymentWithMollie) {
				a.push(
					{
						text: this.$ct("enable-recurring-payments").toString(),
						value: this.payRecurring,
						icon: "credit-card"
					}
				);
			}

			if (this.canCancelAutomaticPaymentWithMollie) {
				a.push(
					{
						text: this.$ct("disable-recurring-payments").toString(),
						value: this.disableRecurringPayments,
						icon: "credit-card"
					}
				);
			}

			if (this.canSign) {
				a.push(
					{
						text: this.$ct("common.sign").toString(),
						value: this.showSignOpenModal,
						icon: "pen"
					}
				);
			}

			return a;
		},
		saleName(): String {
			return this.sale.Type.Name.getTranslation(this.sale.getLng());
		},
		completed(): boolean {
			return this.sale.TotalOpen == 0 && this.sale.TypeID == 1;
		},
		token(): string {
			return this.$route.params.token;
		},
		qrImageUrl(): string {
			if (!this.canPayWithQrCode) {
				return "";
			}
			return ClientPortalService.getSalePaymentQrCodeUrl(this.token);
		},
		canCreateAutomaticPaymentWithMollie(): boolean {
			return this.mollieAutomaticPaymentStatus == MollieAutomaticPaymentStatus.NOT_CONFIGURED;
		},
		canCancelAutomaticPaymentWithMollie(): boolean {
			return this.mollieAutomaticPaymentStatus == MollieAutomaticPaymentStatus.CONFIGURED;

		},
		shouldFetchAsImage():boolean {
			return BrowserInfo.isMobile() || BrowserInfo.isIPadOS();
		},
		computedFriendlyId():string {
			return this.sale.getOriginalComputedFriendlyIDFromSaldoDocument();
		},
		isMobile():boolean {
			return BrowserInfo.isMobile();
		},
		hasTicket():boolean{
			return this.sale.LastBank?.CtepSaleResult?.ClientTicket !== undefined;
		},
		ticket():string{
			return this.sale.LastBank?.CtepSaleResult?.ClientTicket || "";
		}
	},
	methods: {
		async pay() {
			await ClientPortalService.paySale(this.token);
		},
		async payRecurring() {
			await ClientPortalService.paySale(this.token, {enableRecurringPayment: true});
		},
		async disableRecurringPayments() {
			await ClientPortalService.disableRecurringPayments(this.token);
			if (this.sale.TotalOpen != 0) {
				this.mollieAutomaticPaymentStatus = MollieAutomaticPaymentStatus.NOT_CONFIGURED;
			} else {
				this.mollieAutomaticPaymentStatus = MollieAutomaticPaymentStatus.UNAVAILABLE;
			}
		},
		async downloadFile() {
			let result = await ClientPortalService.downloadSaleAsBlob(this.token);
			downloadBlob(base64ToBlob(result.Data, result.MimeType), result.FileName);
		},
		async print() {
			let frame = this.$refs.pdfViewer as any;;
			if (!frame) return;
			frame.print();
		},
		async fetch() {
			let result = await ClientPortalService.getSaleFromPublicToken(this.token);
			this.sale = result.sale;
			this.canPayWithMollie = result.canPayWithMollie;
			this.canPayWithQrCode = result.canPayWithQrCode;
			this.hideToPayAmount = result.hideToPayAmount;
			this.mollieAutomaticPaymentStatus = result.mollieAutomaticPaymentStatus;
			this.canSign = result.canSign;
		},
		async fetchPdf(useImages:boolean){
			if (!useImages){
				let result = await ClientPortalService.downloadSaleAsBlob(this.token);
				this.blob = base64ToBlob(result.Data, result.MimeType);
			}else{
				let result = await ClientPortalService.downloadSaleAsImages(this.token);
				this.pdfImages = result.Data;
			}
		},
		async showSignOpenModal() {
			this.signModalOpen = true;
		}
	},
	async mounted() {
		await this.fetch();
		let splashEl = document.getElementById("splash");
		if (!splashEl) return;
		splashEl.remove();
	},

	components: {
		AppSignSaleModal,
		AppPdfViewer,
		AppTicketPreview
	},
	i18nEx: {
		componentPrefix: "components.client-portal"
	}
});
